import React, { useCallback, useEffect, useState } from 'react';
import useForm from 'react-hook-form';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { Box, Grid } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { handleSelectProperty } from '../../../../../redux/modules/propertyClaim';
import PropertyList from '../../../components/PropertyList';
import ClaimStepNavigationWrapper from '../../../../../components/ClaimFormComponents/ClaimStepNavigationWrapper';
import DateInputFieldWithValidation from '../../../../../components/ValidationFormElements/DateInputFieldWithValidation';
import OtherPhysicalApplicantForm from '../../../../CompensationContainer/components/OtherPhysicalApplicantForm';
import FormSection from '../../../../../components/FormComponents/FormSection';
import TextInputFieldWithValidation from '../../../../../components/ValidationFormElements/TextInputFieldWithValidation';
import ChangeApplicant from '../../../../../components/ClaimFormComponents/ChangeApplicant';
import OtherLegalApplicantForm from '../../../../../components/FormComponents/OtherLegalApplicantForm';
import { physicalPersonalDataSchema } from '../../../../../validations/claimValidationSchema/property/propertyClaimValidationSchema';
import useRegisterValidationFields from '../../../../../hooks/useRegisterValidationFields';
import useDeviceResize from '../../../../../hooks/useDeviceResize';
import useUnRegisterValidationFields from '../../../../../hooks/useUnRegisterValidationFields';
import { APPLICANT_IS, PHYSICAL_PERSONAL_INFORMATION_FIELDS } from '../../../options';
import { LANGUAGES } from '../../../../../constants';

import ApplicantIcon from '../../../../../img/formSectionIcons/applicant.svg';
import VitimInfoIcon from '../../../../../img/formSectionIcons/victimInfo.svg';
import DamagedPropertyIcon from '../../../../../img/formSectionIcons/damagedProperties.svg';

const PhysicalPersonalInformation = ({
  initialData,
  formLanguage,
  onFormSubmit,
  setStepNavigationBox,
  goPrevious,
  countries,
}) => {
  const {
    handleSubmit,
    register,
    setValue,
    errors,
    clearError,
    unregister,
    triggerValidation,
    getValues,
  } = useForm({
    validationSchema: physicalPersonalDataSchema,
    defaultValues: initialData ? { ...initialData } : {},
    mode: 'onBlur',
  });

  useRegisterValidationFields(PHYSICAL_PERSONAL_INFORMATION_FIELDS, register);
  useUnRegisterValidationFields(PHYSICAL_PERSONAL_INFORMATION_FIELDS, unregister);

  const isDeviceLgSize = useDeviceResize('lg');
  const dispatch = useDispatch();
  const selectedProperties = useSelector((state) => state.propertyClaim.selectedProperties);

  const { applicant, isArmCitizen } = initialData;

  const [applicantIs, setApplicantIs] = useState(APPLICANT_IS.owner.value);
  const [validUntilDate, setValidUntilDate] = useState('');
  const [givenDate, setGivenDate] = useState('');

  useEffect(() => {
    setValue('isCitizen', isArmCitizen);
  }, [isArmCitizen, setValue]);

  useEffect(() => {
    setValue('applicantIs', applicantIs);
  }, [applicantIs, setValue]);

  useEffect(() => {
    setValue('selectedProperties', selectedProperties, !!errors.selectedProperties);
  }, [selectedProperties, setValue, errors.selectedProperties]);

  useEffect(() => {
    setValue('formLanguage', formLanguage);
    setValue('validUntilDate', getValues().passportValidUntil, !!getValues().passportValidUntil);
    setValue('passportGiven', getValues().passportGiven, !!getValues().passportGiven);
    setValue('address', getValues().address, !!getValues().address);
  }, [formLanguage, setValue, getValues]);

  useEffect(() => {
    setValue('passportGiven', applicant.passportGiven, !!applicant.passportGiven);
    setValue('passportValidUntil', applicant.passportValidUntil, !!applicant.passportValidUntil);

    setGivenDate(applicant.passportGiven);
    setValidUntilDate(applicant.passportValidUntil);
  }, [applicant, formLanguage, setValue]);

  useEffect(() => {
    if (initialData.realEstates.length === 1 && !initialData.properties.length) {
      dispatch(handleSelectProperty(initialData.realEstates[0].propertyRealEstateId));
    }
  }, [initialData, dispatch]);

  useEffect(() => {
    if (initialData.properties.length === 1 && !initialData.realEstates.length) {
      dispatch(handleSelectProperty(initialData.properties[0].propertyId));
    }
  }, [initialData, dispatch]);

  const handleChange = (target) => {
    setValue(target.name, target.value);
  };

  const updateGivenDate = useCallback(
    (newDate) => {
      setGivenDate(newDate ? format(newDate, 'yyyy-MM-dd') : null);
      setValue('passportGiven', newDate ? format(newDate, 'yyyy-MM-dd') : null);
    },
    [setValue],
  );

  const updateValidUntilDate = useCallback(
    (newDate) => {
      setValidUntilDate(newDate ? format(newDate, 'yyyy-MM-dd') : null);
      setValue('passportValidUntil', newDate ? format(newDate, 'yyyy-MM-dd') : null);
    },
    [setValue],
  );

  const onSubmit = (data) => {
    onFormSubmit(data);
  };

  return (
    <Box>
      <ClaimStepNavigationWrapper
        clearError={clearError}
        errors={errors}
        isNextDisabled={false}
        handleSubmit={handleSubmit}
        setStepNavigationBox={setStepNavigationBox}
        isDeviceLgSize={isDeviceLgSize}
        onSubmit={onSubmit}
        back={goPrevious}
        dependencies={[JSON.stringify(errors), isDeviceLgSize, false]}
      />
      <FormSection icon={VitimInfoIcon} title="propertyClaim.personalData.affectedRowLbl">
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              name={`firstName${formLanguage.toUpperCase()}`}
              labelKey="claim.personalData.affectedFName"
              autoComplete="off"
              register={register}
              handleChange={({ target }) => {
                handleChange(target);
              }}
              disabled={formLanguage === LANGUAGES.HY && !errors[`firstName${formLanguage.toUpperCase()}`]}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              name={`lastName${formLanguage.toUpperCase()}`}
              autoComplete="off"
              labelKey="claim.personalData.affectedLName"
              register={register}
              handleChange={({ target }) => {
                handleChange(target);
              }}
              disabled={formLanguage === LANGUAGES.HY && !errors[`lastName${formLanguage.toUpperCase()}`]}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              name="passport"
              labelKey="claim.personalData.passNum"
              autoComplete="off"
              handleChange={({ target }) => handleChange(target)}
              register={register}
              disabled
            />
          </Grid>
          {!isArmCitizen && (
            <div>
              <Grid item xs={12} md={4}>
                <DateInputFieldWithValidation
                  formProps={{ errors, clearError }}
                  labelKey="claim.personalData.givenDate"
                  name="passportGiven"
                  value={givenDate ? new Date(givenDate) : null}
                  handleChange={(date) => {
                    // eslint-disable-next-line no-restricted-globals
                    if (date instanceof Date && !isNaN(date)) {
                      updateGivenDate(date);
                    }
                  }}
                  maxDate={new Date()}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DateInputFieldWithValidation
                  formProps={{ errors, clearError }}
                  labelKey="cmtplForm.personalData.validUntilDate"
                  name="passportValidUntil"
                  value={validUntilDate ? new Date(validUntilDate) : null}
                  handleChange={(date) => {
                    // eslint-disable-next-line no-restricted-globals
                    if (date instanceof Date && !isNaN(date)) {
                      updateValidUntilDate(date);
                    }
                  }}
                  minDate={new Date()}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextInputFieldWithValidation
                  formProps={{ errors, clearError }}
                  labelKey="cmtplForm.personalData.passportFrom"
                  name="passportFrom"
                  autoComplete="off"
                  register={register}
                  handleChange={({ target }) => handleChange(target.value)}
                />
              </Grid>
            </div>
          )}
          <Grid item xs={12} md={8}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              name={`physAddress${formLanguage.toUpperCase()}`}
              labelKey="propertyClaim.personalData.address"
              autoComplete="off"
              register={register}
              handleChange={({ target }) => {
                handleChange(target);
              }}
              disabled={formLanguage === LANGUAGES.HY && !errors[`physAddress${formLanguage.toUpperCase()}`]}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              name="contractTerm"
              labelKey="propertyClaim.personalData.contractTerm"
              autoComplete="off"
              register={register}
              handleChange={({ target }) => {
                handleChange(target);
              }}
              disabled
            />
          </Grid>
        </Grid>
      </FormSection>
      <FormSection icon={ApplicantIcon} title="personalClaim.personalData.applicantIs">
        <ChangeApplicant
          primaryOption={{
            primaryLabel: 'propertyClaim.personalData.owner',
            primaryValue: 'owner',
          }}
          value={applicantIs}
          onChange={(value) => {
            setApplicantIs(value);
            setValue('applicantIs', value);
          }}
        />
        {applicantIs === APPLICANT_IS.other.value && (
          <OtherPhysicalApplicantForm
            initialData={initialData}
            formProps={{
              errors,
              clearError,
              getValues,
              setValue,
              register,
              unregister,
              triggerValidation,
            }}
            countries={countries}
          />
        )}
        {applicantIs === APPLICANT_IS.otherLegal.value && (
          <OtherLegalApplicantForm
            initialData={{ ...initialData, applicantIs }}
            formProps={{
              register,
              setValue,
              errors,
              clearError,
              triggerValidation,
            }}
            valueNames={{
              hvhh: 'applicantOrganizationHvhh',
              organizationName: `applicantOrganizationName${formLanguage.toUpperCase()}`,
              email: 'applicantOrganizationEmail',
              phone: 'applicantOrganizationPhone',
            }}
          />
        )}
      </FormSection>
      <PropertyList
        realEstates={initialData.realEstates}
        properties={initialData.properties}
        selected={selectedProperties}
        icon={DamagedPropertyIcon}
        onSelect={(pr) => {
          dispatch(handleSelectProperty(pr));
        }}
        errors={errors}
      />

    </Box>
  );
};

PhysicalPersonalInformation.propTypes = {
  initialData: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    passport: PropTypes.string,
    isArmCitizen: PropTypes.bool,
    passportFrom: PropTypes.string,

    transportModel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    vin: PropTypes.string,
    applicant: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      passportGiven: PropTypes.string,
      passportValidUntil: PropTypes.string,
    }),
    realEstates: PropTypes.arrayOf(
      PropTypes.shape({
        propertyRealEstateId: PropTypes.string,
      }),
    ),
    properties: PropTypes.arrayOf(
      PropTypes.shape({
        propertyId: PropTypes.string,
      }),
    ),
  }),
  onFormSubmit: PropTypes.func,
  formLanguage: PropTypes.string,
  setStepNavigationBox: PropTypes.func.isRequired,
  goPrevious: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

PhysicalPersonalInformation.defaultProps = {
  initialData: null,
  onFormSubmit: () => { },
  formLanguage: null,
};

export default PhysicalPersonalInformation;
